'use client'

import { useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import { useNewsList } from '@/hook/news/use-news-list'
import { buildAssetUrl } from '@/utils/api-url'
import { Pagination } from '@heroui/react'
import { motion } from 'framer-motion'
import { Activity, Calendar, Loader2, Search, BookOpen, PartyPopper } from 'lucide-react'
import { NewsCategory, NEWS_CATEGORY_LABELS } from '@/types/news'

import Image from '@/components/Image'

const ITEMS_PER_PAGE = 11

// Hero Component với animated images và decorative elements
const ActivitiesHero = ({
  featuredImages,
  totalItems,
}: {
  featuredImages: string[]
  totalItems: number
}) => {
  const imageStyles = useMemo(() => {
    return featuredImages.slice(0, 6).map(() => {
      const xRange = Math.random() * 60 - 30
      const yRange = Math.random() * 60 - 30
      const rotateRange = Math.random() * 15 - 7.5

      return {
        initial: {
          top: `${Math.random() * 85}%`,
          left: `${Math.random() * 85}%`,
        },
        animate: {
          x: [0, xRange, -xRange, xRange, 0],
          y: [0, yRange, 0, -yRange, 0],
          rotate: [0, rotateRange, -rotateRange, 0, rotateRange],
        },
      }
    })
  }, [featuredImages])

  const MotionImage = ({ src, initial, animate }: { src: string; initial: any; animate: any }) => (
    <motion.div
      className='absolute h-40 w-60 overflow-hidden rounded-2xl shadow-2xl'
      initial={initial}
      animate={animate}
      transition={{
        duration: Math.random() * 15 + 20,
        repeat: Infinity,
        repeatType: 'reverse',
        ease: 'easeInOut',
      }}
    >
      <Image
        src={src}
        alt=''
        fill
        className='saturate-125 object-cover opacity-25 blur-sm brightness-110'
        unoptimized
      />
    </motion.div>
  )

  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className='relative flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#33B54A] via-[#2EA043] to-[#F78F1E] py-36 md:py-52'
    >
      {/* Animated Images Backdrop */}
      <div className='absolute inset-0 overflow-hidden'>
        {featuredImages.slice(0, 6).map((src, i) => (
          <MotionImage
            key={i}
            src={src}
            initial={imageStyles[i].initial}
            animate={imageStyles[i].animate}
          />
        ))}
      </div>

      {/* Decorative geometric shapes */}
      <div className='pointer-events-none absolute inset-0'>
        {/* Floating circles */}
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className='absolute rounded-full border-2 border-white/20'
            style={{
              width: Math.random() * 100 + 50,
              height: Math.random() * 100 + 50,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, Math.random() * 30 - 15, 0],
              x: [0, Math.random() * 30 - 15, 0],
              opacity: [0.2, 0.4, 0.2],
            }}
            transition={{
              duration: Math.random() * 10 + 15,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        ))}

        {/* Background blur orbs */}
        <motion.div
          className='absolute left-0 top-0 size-96 rounded-full bg-white/10 blur-3xl'
          animate={{
            scale: [1, 1.2, 1],
            x: [0, 50, 0],
            y: [0, 30, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className='absolute bottom-0 right-0 size-96 rounded-full bg-white/10 blur-3xl'
          animate={{
            scale: [1, 1.3, 1],
            x: [0, -50, 0],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      </div>

      <div className='relative z-10 mx-auto max-w-5xl px-4 text-center'>
        <motion.h1
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className='mb-6 text-5xl font-black leading-tight text-white drop-shadow-2xl md:text-6xl lg:text-7xl'
        >
          <span className='block text-[#F78F1E] drop-shadow-lg'>Hoạt động của bé</span>
          <span className='mt-2 block text-white drop-shadow-lg'>tại Trường Mầm Non Trúc Xinh</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3, ease: 'easeOut' }}
          className='mx-auto mb-8 max-w-2xl text-lg leading-relaxed text-white/95 md:text-xl'
        >
          Cùng khám phá những câu chuyện, những nụ cười và những kỷ niệm tuyệt vời được ghi lại mỗi
          ngày
        </motion.p>

        {/* Decorative elements */}
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className='mt-8 flex justify-center gap-3'
        >
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={i}
              className='size-2 rounded-full bg-white/60'
              animate={{
                scale: [1, 1.5, 1],
                opacity: [0.6, 1, 0.6],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                delay: i * 0.2,
                ease: 'easeInOut',
              }}
            />
          ))}
        </motion.div>
      </div>

      {/* Bottom curved edge */}
      <div className='pointer-events-none absolute inset-x-0 bottom-0'>
        <svg viewBox='0 0 1440 200' className='h-20 w-full md:h-24' preserveAspectRatio='none'>
          <path fill='white' d='M0,0 Q720,150 1440,0 L1440,200 L0,200 Z' />
        </svg>
      </div>
    </motion.section>
  )
}

// Filter Section - Gộp Category, Search và Sort
const FilterSection = ({
  selectedCategory,
  onCategoryChange,
  searchQuery,
  onSearchChange,
  sortOrder,
  onSortChange,
}: {
  selectedCategory: NewsCategory | undefined
  onCategoryChange: (category: NewsCategory | undefined) => void
  searchQuery: string
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  sortOrder: string
  onSortChange: (order: string) => void
}) => {
  const categories: { key: NewsCategory | undefined; label: string; icon: React.ReactNode }[] = [
    { key: undefined, label: 'Tất cả', icon: <Activity className='size-4' /> },
    { key: 'hoc_tap_vui_choi', label: 'Học tập vui chơi', icon: <BookOpen className='size-4' /> },
    { key: 'le_hoi_su_kien', label: 'Lễ hội sự kiện', icon: <PartyPopper className='size-4' /> },
  ]

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className='mb-8'
    >
      {/* Main Filter Row */}
      <div className='flex flex-col gap-4 rounded-2xl bg-gray-50/80 p-4 shadow-sm lg:flex-row lg:items-center lg:justify-between'>
        {/* Category Tabs */}
        <div className='flex flex-wrap items-center gap-2'>
          {categories.map((cat) => (
            <button
              key={cat.key ?? 'all'}
              onClick={() => onCategoryChange(cat.key)}
              className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-all duration-200 ${
                selectedCategory === cat.key
                  ? 'bg-gradient-to-r from-[#33B54A] to-[#2EA043] text-white shadow-md'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {cat.icon}
              {cat.label}
            </button>
          ))}
        </div>

        {/* Search & Sort */}
        <div className='flex flex-col items-stretch gap-3 sm:flex-row sm:items-center'>
          {/* Search Input */}
          <div className='relative'>
            <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
              <Search className='size-4 text-gray-400' />
            </div>
            <input
              type='text'
              placeholder='Tìm kiếm...'
              value={searchQuery}
              onChange={onSearchChange}
              className='w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm transition-all focus:border-[#33B54A] focus:outline-none focus:ring-2 focus:ring-[#33B54A]/20 sm:w-48 lg:w-56'
            />
          </div>

          {/* Sort Buttons */}
          <div className='flex items-center gap-1 rounded-lg bg-white p-1 border border-gray-200'>
            <button
              onClick={() => onSortChange('newest')}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
                sortOrder === 'newest'
                  ? 'bg-[#33B54A] text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Mới nhất
            </button>
            <button
              onClick={() => onSortChange('oldest')}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
                sortOrder === 'oldest'
                  ? 'bg-[#33B54A] text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Cũ nhất
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  )
}

// Activity Card - Đẹp và hiện đại
const ActivityCard = ({
  newsItem,
  index,
  isFeatured = false,
  sortOrder = 'newest',
}: {
  newsItem: any
  index: number
  isFeatured?: boolean
  sortOrder?: string
}) => {
  const contentAssets = newsItem.content_assets || []
  const videoAsset = contentAssets.find((asset: any) =>
    asset.asset?.mime_type?.startsWith('video/')
  )
  const imageAsset = contentAssets.find((asset: any) =>
    asset.asset?.mime_type?.startsWith('image/')
  )
  const thumbnail = newsItem.thumbnail || (imageAsset ? imageAsset.asset.url : '')
  const thumbnailUrl = thumbnail ? buildAssetUrl(thumbnail) : '/assets/images/ex1.jpg'
  const videoUrl = videoAsset?.asset?.url ? buildAssetUrl(videoAsset.asset.url) : null
  const description = newsItem.excerpt || newsItem.shortDescription || ''
  const category = newsItem.category as NewsCategory | undefined

  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.95 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true }}
      transition={{
        type: 'spring',
        stiffness: 100,
        damping: 20,
        delay: index * 0.05,
      }}
      whileHover={{
        y: -12,
        scale: 1.02,
        transition: {
          type: 'spring',
          stiffness: 300,
          damping: 30,
        },
      }}
      className={`group relative overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-lg transition-all duration-500 hover:shadow-2xl ${
        isFeatured ? 'md:col-span-2 md:row-span-1' : ''
      }`}
    >
      {/* Decorative gradient overlay on hover */}
      <div className='pointer-events-none absolute inset-0 z-10 rounded-2xl bg-gradient-to-br from-[#33B54A]/0 via-[#33B54A]/0 to-[#F78F1E]/0 transition-all duration-500 group-hover:from-[#33B54A]/5 group-hover:via-[#33B54A]/0 group-hover:to-[#F78F1E]/5' />

      <Link href={`/activities/detail/${newsItem.slug}`}>
        <div
          className={`relative overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 ${
            isFeatured ? 'h-64 md:h-72' : 'h-56'
          }`}
        >
          {videoUrl ? (
            <>
              <video
                src={videoUrl}
                className='size-full object-cover transition-transform duration-700 ease-out group-hover:scale-110'
                muted
                playsInline
                loop
              />
              <div className='absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent' />
              <div className='absolute inset-0 flex items-center justify-center'>
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  className='flex size-20 items-center justify-center rounded-full border-4 border-white/50 bg-white/95 shadow-2xl backdrop-blur-sm'
                >
                  <svg
                    className='ml-1 size-10 text-[#33B54A]'
                    fill='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path d='M8 5v14l11-7z' />
                  </svg>
                </motion.div>
              </div>
            </>
          ) : (
            <>
              <Image
                src={thumbnailUrl}
                alt={newsItem.title}
                fill
                className='object-cover transition-transform duration-700 ease-out group-hover:scale-110'
                unoptimized
              />
              <div className='absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100' />
            </>
          )}

          {/* Category badge */}
          {category && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: index * 0.1 + 0.2 }}
              className='absolute right-4 top-4 z-20'
            >
              <span className={`inline-flex items-center rounded-full border border-white/20 px-3 py-1.5 text-xs font-bold text-white shadow-xl backdrop-blur-sm ${
                category === 'hoc_tap_vui_choi' 
                  ? 'bg-gradient-to-r from-blue-500 to-blue-600' 
                  : 'bg-gradient-to-r from-purple-500 to-pink-500'
              }`}>
                {category === 'hoc_tap_vui_choi' ? <BookOpen className='mr-1.5 size-3.5' /> : <PartyPopper className='mr-1.5 size-3.5' />}
                {NEWS_CATEGORY_LABELS[category]}
              </span>
            </motion.div>
          )}

          {/* Featured badge - Mới nhất hoặc Cũ nhất */}
          {isFeatured && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: index * 0.1 + 0.3 }}
              className='absolute left-4 top-4 z-20'
            >
              <span className={`inline-flex items-center rounded-full border border-white/20 px-4 py-1.5 text-xs font-bold text-white shadow-xl backdrop-blur-sm ${
                sortOrder === 'newest' 
                  ? 'bg-gradient-to-r from-[#33B54A] to-[#2EA043]' 
                  : 'bg-gradient-to-r from-[#F78F1E] to-[#E67E0D]'
              }`}>
                <Activity className='mr-1.5 size-3.5' />
                {sortOrder === 'newest' ? 'Mới nhất' : 'Cũ nhất'}
              </span>
            </motion.div>
          )}

          {/* Shine effect on hover */}
          <div className='absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-1000 ease-in-out group-hover:translate-x-full' />
        </div>
      </Link>

      <div className='relative z-10 bg-white p-6'>
        <h2
          className='text-lg mb-3 line-clamp-2 font-bold text-gray-900 transition-colors duration-300 group-hover:text-[#33B54A]'
        >
          <Link href={`/activities/detail/${newsItem.slug}`} className='hover:underline'>
            {newsItem.title}
          </Link>
        </h2>
        {description && (
          <p className='mb-4 text-gray-600 line-clamp-2 text-sm leading-relaxed'>
            {description}
          </p>
        )}
        <div className='flex items-center gap-2 border-t border-gray-100 pt-2 text-xs text-gray-500'>
          <Calendar className='size-4 text-[#33B54A]' />
          <span className='font-medium'>
  {newsItem.created_at 
    ? new Date(newsItem.created_at).toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit', 
        year: 'numeric'
      })
    : 'Hoạt động mới'}
</span>
        </div>
      </div>
    </motion.div>
  )
}

const ActivitiesPage = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [sortOrder, setSortOrder] = useState('newest')
  const [selectedCategory, setSelectedCategory] = useState<NewsCategory | undefined>(undefined)
  const searchInputRef = useRef<HTMLDivElement>(null)

  const { data, isLoading, error } = useNewsList({
    page: currentPage,
    pageSize: ITEMS_PER_PAGE,
    category: selectedCategory,
  })

  const filteredNews = useMemo(() => {
    if (!data?.items) return []

    let filtered = data.items.filter(
      (item) =>
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.shortDescription?.toLowerCase().includes(searchQuery.toLowerCase())
    )

    if (sortOrder === 'oldest') {
      filtered = [...filtered].reverse()
    }

    return filtered
  }, [data?.items, searchQuery, sortOrder])

  // Get featured images for hero
  const featuredImages = useMemo(() => {
    if (!data?.items)
      return ['/assets/images/ex1.jpg', '/assets/images/ex2.jpg', '/assets/images/ex3.jpg']

    const images: string[] = []
    data.items.forEach((item) => {
      const contentAssets = item.content_assets || []
      const imageAsset = contentAssets.find((asset: any) =>
        asset.asset?.mime_type?.startsWith('image/')
      )
      const thumbnail = item.thumbnail || (imageAsset ? imageAsset.asset.url : '')
      if (thumbnail) {
        images.push(buildAssetUrl(thumbnail))
      }
    })

    // Fallback to default images if not enough
    while (images.length < 6) {
      images.push('/assets/images/ex1.jpg', '/assets/images/ex2.jpg', '/assets/images/ex3.jpg')
    }

    return images.slice(0, 6)
  }, [data?.items])

  const totalPages = data?.total_pages || 1
  const totalItems = data?.total || 0

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    setCurrentPage(1)
  }

  const handleCategoryChange = (category: NewsCategory | undefined) => {
    setSelectedCategory(category)
    setCurrentPage(1)
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    setTimeout(() => {
      if (searchInputRef.current) {
        const header = document.querySelector('header')
        const headerHeight = header ? header.offsetHeight : 100
        const elementPosition = searchInputRef.current.getBoundingClientRect().top
        const offsetPosition = elementPosition + window.pageYOffset - headerHeight - 20
        window.scrollTo({
          top: Math.max(0, offsetPosition),
          behavior: 'smooth',
        })
      }
    }, 100)
  }

  return (
    <div className='bg-white text-gray-800 antialiased'>
      <ActivitiesHero featuredImages={featuredImages} totalItems={totalItems} />

      <main className='relative bg-white pb-20 pt-4 md:pb-28 md:pt-6'>
        <div className='container mx-auto px-4'>
          {/* Filter Section */}
          <div ref={searchInputRef}>
            <FilterSection
              selectedCategory={selectedCategory}
              onCategoryChange={handleCategoryChange}
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              sortOrder={sortOrder}
              onSortChange={setSortOrder}
            />
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className='flex items-center justify-center py-20'>
              <Loader2 className='size-10 animate-spin text-[#33B54A]' />
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className='py-20 text-center'>
              <p className='text-lg text-red-500'>
                Đã xảy ra lỗi khi tải hoạt động. Vui lòng thử lại sau.
              </p>
            </div>
          )}

          {/* Grid Layout - Cân đối và đẹp */}
          {!isLoading && !error && (
            <>
              {filteredNews.length > 0 ? (
                <>
                  <div className='mb-12 grid grid-cols-1 gap-6 md:grid-cols-3'>
                    {filteredNews.map((newsItem, index) => (
                      <ActivityCard
                        key={newsItem.id || newsItem.public_id || index}
                        newsItem={newsItem}
                        index={index}
                        isFeatured={index === 0}
                        sortOrder={sortOrder}
                      />
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className='mt-8 flex justify-center'>
                      <Pagination
                        isCompact
                        showControls
                        showShadow
                        color='primary'
                        page={currentPage}
                        total={totalPages}
                        onChange={handlePageChange}
                      />
                    </div>
                  )}
                </>
              ) : (
                <div className='py-20 text-center'>
                  <p className='text-lg text-gray-500'>Không tìm thấy hoạt động nào.</p>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  )
}

export default ActivitiesPage
